<template>
  <DropdownMenuRoot>
    <DropdownMenuTrigger
      class="min-w-[100px] box-border flex items-center justify-between gap-2 overflow-hidden rounded-md border border-solid px-2 font-inter text-xs font-medium leading-[30px] outline-none transition duration-300 group-focus-within:outline group-focus-within:outline-2 group-focus-within:outline-accent-1"
      :class="{
        'border-gray-600 bg-gray-600 text-gray-50 group-focus-within:text-gray-10 group-hover:border-gray-400':
          !inverse,
        'border-[color-mix(in_srgb,var(--c-g100)_40%,var(--c-g10))] bg-[color-mix(in_srgb,var(--c-g100)_40%,var(--c-g10))] text-gray-400 group-focus-within:text-gray-700 group-hover:border-[color-mix(in_srgb,var(--c-g100)_70%,var(--c-g10))]':
          inverse,
      }"
      :data-testid="dataTestid"
      v-tooltip="{ content: tooltip }"
    >
      {{ options.find((option) => option.isActive)?.text || options[0].text }}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 320 512"
        width="7.5"
        height="12"
      >
        <path
          d="M137.4 374.6c12.5 12.5 32.8 12.5 45.3 0l128-128c9.2-9.2 11.9-22.9 6.9-34.9s-16.6-19.8-29.6-19.8L32 192c-12.9 0-24.6 7.8-29.6 19.8s-2.2 25.7 6.9 34.9l128 128z"
          fill="currentColor"
        />
      </svg>
    </DropdownMenuTrigger>

    <DropdownMenuPortal>
      <DropdownMenuContent
        class="tiptap-mt-drop max-h-[300px] overflow-y-auto outline-none fotn-inter z-[9999] rounded-md shadow-[0px_10px_38px_-10px_rgba(22,_23,_24,_0.35),_0px_10px_20px_-15px_rgba(22,_23,_24,_0.2)] will-change-[opacity,transform] data-[side=top]:animate-slideDownAndFade data-[side=right]:animate-slideLeftAndFade data-[side=bottom]:animate-slideUpAndFade data-[side=left]:animate-slideRightAndFade"
        :side-offset="2"
        :data-testid="dataTestid + '-content'"
        align="start"
        :collision-padding="{ top: 10, right: 10, bottom: 10, left: 10 }"
        :avoid-collisions="true"
        sticky="partial"
      >
        <DropdownMenuItem
          v-for="option in options"
          :key="option.format"
          textValue=""
          class="tiptap-mt-drop-item flex cursor-pointer items-center leading-6 px-3 py-1 transition duration-300"
          :class="{
            'bg-gray-900 hover:bg-gray-400 text-gray-100 hover:text-gray-50':
              !inverse,
            'bg-gray-10 hover:bg-[color-mix(in_srgb,var(--c-g100)_40%,var(--c-g10))] text-gray-400 hover:text-gray-600':
              inverse,
            '!bg-gray-500 !text-gray-10': option.isActive && !inverse,
            '!bg-[color-mix(in_srgb,var(--c-g100)_60%,var(--c-g10))] !text-gray-500':
              option.isActive && inverse,
          }"
          @select="clickToOption(option)"
        >
          <component
            :is="option.format"
            class="inline-block whitespace-nowrap"
            :style="option.style"
            >{{ option.text }}</component
          >
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenuPortal>
  </DropdownMenuRoot>
</template>

<script lang="ts" setup>
import {
  DropdownMenuItem,
  DropdownMenuContent,
  DropdownMenuPortal,
  DropdownMenuRoot,
  DropdownMenuTrigger,
} from "reka-ui";
import { onClickOutside } from "@vueuse/core";
import { ref } from "vue";

export type DropOption = {
  format: string;
  style?: string;
  text: string;
  set: () => void;
  isActive: boolean;
};

defineProps<{
  options: DropOption[];
  dataTestid?: string;
  inverse?: boolean;
  tooltip?: string;
}>();

const isOpen = ref(false);
const dropdown = ref<HTMLElement>();

onClickOutside(dropdown, () => {
  isOpen.value = false;
});

function clickToOption(option: DropOption) {
  option.set();
  isOpen.value = false;
}
</script>
